// Uniform spatial hash for neighbour queries.
//
// This is the actual reason the sim holds 60 Hz with a large flock — not "planar distance is cheap".
// Naive boids is O(n²): 200 sheep × 3 rules × 60 Hz is ~2.4M pair-checks/second before rendering,
// which survives on desktop and struggles on a mid-range phone. Bucketing by cell makes neighbour
// queries effectively O(n·k).
//
// Cell size must be >= the largest perception radius, or a query will miss neighbours that a 3×3
// cell sweep should have caught.

import type { Vec2 } from './vec2'

export class SpatialGrid {
  readonly cellSize: number
  readonly cols: number
  readonly rows: number
  /** cells[i] holds indices into the entity array. Reused across ticks; never reallocated. */
  private readonly cells: number[][]

  constructor(worldWidth: number, worldHeight: number, cellSize: number) {
    this.cellSize = cellSize
    this.cols = Math.max(1, Math.ceil(worldWidth / cellSize))
    this.rows = Math.max(1, Math.ceil(worldHeight / cellSize))
    this.cells = new Array(this.cols * this.rows)
    for (let i = 0; i < this.cells.length; i++) this.cells[i] = []
  }

  clear(): void {
    for (let i = 0; i < this.cells.length; i++) this.cells[i].length = 0
  }

  private cellIndex(x: number, y: number): number {
    let cx = Math.floor(x / this.cellSize)
    let cy = Math.floor(y / this.cellSize)
    if (cx < 0) cx = 0
    else if (cx >= this.cols) cx = this.cols - 1
    if (cy < 0) cy = 0
    else if (cy >= this.rows) cy = this.rows - 1
    return cy * this.cols + cx
  }

  insert(index: number, pos: Vec2): void {
    this.cells[this.cellIndex(pos.x, pos.y)].push(index)
  }

  /** Rebuild from scratch. Cheaper than incremental updates at this entity count. */
  rebuild(positions: readonly Vec2[]): void {
    this.clear()
    for (let i = 0; i < positions.length; i++) this.insert(i, positions[i])
  }

  /**
   * Visit every entity index in the 3×3 cell block around `pos`.
   *
   * Iteration order is deterministic (row-major cells, insertion order within a cell) because the
   * grid is rebuilt in entity-index order each tick. Do not change to a Set or Map — neighbour
   * order affects float accumulation order, and therefore replay reproducibility.
   */
  forEachNeighbour(pos: Vec2, visit: (index: number) => void): void {
    const cx = Math.floor(pos.x / this.cellSize)
    const cy = Math.floor(pos.y / this.cellSize)
    const x0 = Math.max(0, cx - 1)
    const x1 = Math.min(this.cols - 1, cx + 1)
    const y0 = Math.max(0, cy - 1)
    const y1 = Math.min(this.rows - 1, cy + 1)

    for (let y = y0; y <= y1; y++) {
      const rowBase = y * this.cols
      for (let x = x0; x <= x1; x++) {
        const bucket = this.cells[rowBase + x]
        for (let i = 0; i < bucket.length; i++) visit(bucket[i])
      }
    }
  }
}
